import { useTranslation } from 'react-i18next';
import UniversalInput from "components/uiComponents/UniversalInput";
import { ReactComponent as RemoveIcon } from "../../../static/icons/cross-circle-gray.svg";
import "./SettingItem.scss";
import { useSelector } from "react-redux";
import DatePicker from "components/uiComponents/Datepicker";
import SidebarButtonDropdown from "components/sidebarComponents/SidebarButtonDropdown";
import SidebarDropdown from "components/sidebarComponents/SidebarDropdown";
import Tag from "components/uiComponents/Tag";
import CountTag from "components/uiComponents/CountTag";
import { useMemo } from "react";
import { conditionsType, keyIndexesForData } from "utils/types";
import useSetting from "components/sidebarComponents/SidebarProject/use-settings";
import { getTemporarySetting } from "store/selectors/templateSettings";
import { ReactComponent as UniqueIcon } from "../../../static/icons/unique-icon.svg";
import Select from "components/uiComponents/Select";
import { valueTypes } from "utils/builder/ResultClasses";

const SettingsItem = ({
  options,
  id,
  currentOption,
  currentColor,
  withColorPicker,
  type,
  inputsDataArray: currentSelectedValue = [],
  deleteItemCallback,
}) => {
  const { t } = useTranslation();
  
  const { updateItem } = useSetting();
  const updateItemHandler = (updateData) => {
    updateItem(type, updateData);
  };
  const deleteItemHandler = () => {
    deleteItemCallback(id);
  };
  const firstValue = currentSelectedValue?.[keyIndexesForData.firstValue];
  const secondValue = currentSelectedValue?.[keyIndexesForData.secondValue];
  const firstDateValue = useMemo(() => new Date(firstValue), [firstValue]);
  const secondDateValue = useMemo(() => new Date(secondValue), [secondValue]);
  const setSelectedOption = (option) => {
    const updateData = {
      id,
      selectedOption: option,
      inputsDataArray: [],
    };
    updateItemHandler(updateData);
  };
  const currentOpenSettings = useSelector(getTemporarySetting);

  const formatDateArray = (currentOpenSettingsProps) => {
    if (currentOpenSettingsProps?.type === valueTypes.customDate || 
        currentOpenSettingsProps?.type === valueTypes.date ||
        currentOpenSettingsProps?.type === valueTypes.proDate) {
      const formattedDates = currentOpenSettingsProps?.existingValue?.map(date => {
        if (date instanceof Date) {
          return date.toDateString();
        }
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          return dateObj.toDateString();
        }
        return date;
      });
      // Remove duplicates and filter out invalid dates
      return formattedDates ? [...new Set(formattedDates.filter(Boolean))] : [];
    } else {
      return currentOpenSettingsProps?.existingValue;
    }
  };
  
  const currentPredictValue = formatDateArray(currentOpenSettings);
  
  const handleUniversalInputChange = (index) => {
    return (value) => {
      let newInputsDataArray = [...currentSelectedValue];
      newInputsDataArray[index] = value;
      const updateData = {
        id,
        inputsDataArray: newInputsDataArray,
      };
      updateItemHandler(updateData);
    };
  };

  const renderColorInput = () => {
    const updateColorValue = (color) => {
      const updateData = {
        id,
        color,
      };
      updateItemHandler(updateData);
    };
    const isUniqueValue = currentOption?.key === conditionsType.uniqueValue;
    const colorInputHandleChange = (event) => {
      updateColorValue(event.currentTarget.value);
    };
    if (isUniqueValue) {
      return <div><UniqueIcon /></div>;
    } else {
      return (
        <input
          type="color"
          value={currentColor}
          onChange={colorInputHandleChange}
        />
      );
    }
  };
  
  const handlePredictValueChange = (value) => {
    const updatedValue = currentSelectedValue.filter((selectedValue) => {
      return selectedValue !== value;
    });
    if (updatedValue.length === currentSelectedValue.length) {
      updatedValue.push(value);
    }
    const updateData = {
      id,
      inputsDataArray: updatedValue,
    };
    updateItemHandler(updateData);
  };
  
  const handleRemoveTag = (value) => {
    handlePredictValueChange(value);
  };

  const renderPredictValueTitle = () => {
    if (!currentSelectedValue?.length) {
      return <div>{t('select_predict_value')}</div>;
    }
    const currentTagItems = currentSelectedValue.slice(0, 2).map((value) => (
      <div key={value} onClick={(event) => event.stopPropagation()}>
        <Tag title={value} onRemoveTag={() => handleRemoveTag(value)} />
      </div>
    ));
    return (
      <div className="predict-select-tag-wrapper">
        {currentTagItems}
        {currentSelectedValue.length > 2 ? (
          <CountTag count={`+${currentSelectedValue.length - 2}`} />
        ) : null}
      </div>
    );
  };

  const clearPredictValuesHandler = () => {
    const updateData = {
      id,
      inputsDataArray: [],
    };
    updateItemHandler(updateData);
  };

  return (
    <div className="filter-setting-item-wrapper">
      <div className="filter-setting-item_top-content">
        <Select
          selectedValue={currentOption}
          setSelectedValue={setSelectedOption}
          isBold={false}
          textSize="md"
          selectSize="sm"
          withAbsolutePosition
          customContainerClass="selectDropDownContainer"
          options={options}
        />
        {withColorPicker ? renderColorInput() : null}
        <div
          className="filter-setting-item-delete-icon"
          onClick={deleteItemHandler}
        >
          <RemoveIcon />
        </div>
      </div>
      {currentOption?.hasInput ? (
        <UniversalInput
          value={firstValue ? firstValue : ""}
          size="sm"
          onChangeValue={handleUniversalInputChange(keyIndexesForData.firstValue)}
        />
      ) : null}
      {currentOption?.hasDatepicker ? (
        <DatePicker
          value={firstDateValue}
          size="sm"
          onChangeValue={handleUniversalInputChange(keyIndexesForData.firstValue)}
        />
      ) : null}
      {currentOption?.hasSecondDatepicker ? (
        <DatePicker
          value={secondDateValue}
          onChangeValue={handleUniversalInputChange(keyIndexesForData.secondValue)}
        />
      ) : null}
      {currentOption?.hasSecondInput ? (
        <UniversalInput
          value={secondValue}
          size="sm"
          onChangeValue={handleUniversalInputChange(keyIndexesForData.secondValue)}
        />
      ) : null}
      {currentOption?.hasDropdown ? (
        <div style={{ backgroundColor: "white", borderRadius: 10 }}>
          <SidebarButtonDropdown
            buttonTitle={renderPredictValueTitle()}
            buttonBorder
            dropdownPosition="top"
            isMaxSmallHeight
            withAbsolutePosition
            withClearBtn={false}
            onClearAll={clearPredictValuesHandler}
          >
            <SidebarDropdown
              optionsType="test"
              commonOptions={currentPredictValue}
              selectedItems={currentSelectedValue}
              onChangeSelectedItems={handlePredictValueChange}
            />
          </SidebarButtonDropdown>
        </div>
      ) : null}
    </div>
  );
};

export default SettingsItem;